import { crsLeadSubmitted } from "@/lib/analytics-events";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Check,
  Upload,
  type LucideIcon,
} from "lucide-react";
import { submitEnquiry } from "@/lib/forms.functions";
import { submitGrantsAssessment } from "@/lib/grants-assessment.functions";
import { submissionSchema } from "@/lib/form-schema";
import {
  GRANTS_ATTACHMENT_ACCEPT,
  GRANTS_ATTACHMENT_MAX_BYTES,
  validateGrantsAttachmentFile,
} from "@/lib/grants-assessment-schema";
import { SERVICES } from "@/lib/services";
import {
  REMEDIAL_WORKS,
  RETROFIT_ENQUIRY_OPTION_LABEL,
  RETROFIT_ENQUIRY_OPTION_VALUE,
  RETROFIT_MEASURE_TILES,
  type IconTile,
} from "@/content/enquiry-retrofit";
import { cn } from "@/lib/utils";

/**
 * Multi-step visual wrapper around the homepage hero survey form.
 *
 * UX matches SolarPvHeroForm (one step at a time) without the “Why Choose CRS” panel.
 * Submission mechanics mirror `<EnquiryForm formType="survey" variant="survey" />`:
 *   { form_type: "survey", name, phone, email, postcode, service_of_interest,
 *     message, source_page, gclid, honeypot }
 * plus the existing retrofit assessment quotation route when selected.
 */

interface Props {
  sourcePage?: string;
  title?: string;
  subtitle?: string;
  className?: string;
}

const TOTAL_STEPS = 4;

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Could not read file"));
        return;
      }
      const base64 = result.split(",")[1];
      if (!base64) {
        reject(new Error("Could not read file"));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

export function HomeHeroForm({
  sourcePage = "/",
  title = "Book Your Free Assessment",
  subtitle = "A member of our team will be in touch shortly to discuss your requirements.",
  className,
}: Props) {
  const submit = useServerFn(submitEnquiry);
  const submitRetrofit = useServerFn(submitGrantsAssessment);
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [postcode, setPostcode] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [remedialWorks, setRemedialWorks] = useState<Set<string>>(new Set());
  const [measures, setMeasures] = useState<Set<string>>(new Set());
  const [assessmentFile, setAssessmentFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const isRetrofit = selectedService === RETROFIT_ENQUIRY_OPTION_VALUE;

  /**
   * Reads the live DOM values out of the form. Browser autofill and typing that
   * happens before React hydrates do not fire onChange, which previously left
   * React state empty and made "Request a callback" fail validation even though
   * the fields visibly contained data.
   */
  function readDomValues() {
    const el = formRef.current;
    if (!el) return null;
    const get = (fieldName: string) => {
      const node = el.elements.namedItem(fieldName) as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement
        | null;
      return typeof node?.value === "string" ? node.value : "";
    };
    return {
      name: get("name"),
      email: get("email"),
      phone: get("phone"),
      postcode: get("postcode"),
      service: get("service_of_interest"),
      message: get("message"),
    };
  }

  /** Adopt any DOM values React does not know about, and return the merged set. */
  function syncFromDom() {
    const dom = readDomValues();
    const merged = {
      name: dom?.name || name,
      email: dom?.email || email,
      phone: dom?.phone || phone,
      postcode: dom?.postcode || postcode,
      service: dom?.service || selectedService,
      message: dom?.message || message,
    };
    if (merged.name !== name) setName(merged.name);
    if (merged.email !== email) setEmail(merged.email);
    if (merged.phone !== phone) setPhone(merged.phone);
    if (merged.postcode !== postcode) setPostcode(merged.postcode);
    if (merged.service !== selectedService) setSelectedService(merged.service);
    if (merged.message !== message) setMessage(merged.message);
    return merged;
  }

  // Pick up pre-hydration input and browser autofill (which lands asynchronously).
  useEffect(() => {
    const adopt = () => syncFromDom();
    adopt();
    const t1 = window.setTimeout(adopt, 300);
    const t2 = window.setTimeout(adopt, 1200);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);


  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const params = new URLSearchParams(window.location.search);
      const gclid = params.get("gclid");
      if (gclid) {
        const payload = JSON.stringify({
          value: gclid,
          expiresAt: Date.now() + 90 * 24 * 60 * 60 * 1000,
        });
        window.localStorage.setItem("crs_gclid", payload);
      }
    } catch {
      /* ignore */
    }
  }, []);

  function getStoredGclid(): string | undefined {
    if (typeof window === "undefined") return undefined;
    try {
      const fromUrl = new URLSearchParams(window.location.search).get("gclid");
      if (fromUrl) return fromUrl;
      const raw = window.localStorage.getItem("crs_gclid");
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as { value?: string; expiresAt?: number };
      if (parsed.value && parsed.expiresAt && parsed.expiresAt > Date.now()) {
        return parsed.value;
      }
    } catch {
      /* ignore */
    }
    return undefined;
  }

  function toggleFromSet(
    setter: (fn: (prev: Set<string>) => Set<string>) => void,
    key: string
  ) {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function onAssessmentFileChange(file: File | null) {
    setErrors((prev) => {
      const next = { ...prev };
      delete next.attachment;
      return next;
    });
    if (!file) {
      setAssessmentFile(null);
      return;
    }
    const validationError = validateGrantsAttachmentFile(file);
    if (validationError) {
      setAssessmentFile(null);
      setErrors((prev) => ({ ...prev, attachment: validationError }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setAssessmentFile(file);
  }

  function validateStep(
    current: number,
    values: { name: string; email: string; phone: string; postcode: string }
  ): boolean {
    const e: Record<string, string> = {};
    if (current === 1) {
      if (!values.name.trim()) e.name = "Please enter your name";
    } else if (current === 2) {
      if (!values.phone.trim()) e.phone = "Please enter your phone number";
      if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
        e.email = "Please enter a valid email address";
      }
    } else if (current === 3) {
      if (!values.postcode.trim()) e.postcode = "Please enter your postcode";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function goNext() {
    if (!validateStep(step, syncFromDom())) return;
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  }
  function goBack() {
    syncFromDom();
    setErrors({});
    setFormError(null);
    setStep((s) => Math.max(s - 1, 1));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const current = syncFromDom();
    if (!validateStep(step, current)) return;
    setStatus("submitting");
    setFormError(null);
    setErrors({});

    const source =
      sourcePage || (typeof window !== "undefined" ? window.location.pathname : "unknown");

    const trimmedName = current.name.trim();
    const trimmedPhone = current.phone.trim();
    const trimmedEmail = current.email.trim();
    const trimmedPostcode = current.postcode.trim();
    const userMessage = current.message.trim();


    if (current.service === RETROFIT_ENQUIRY_OPTION_VALUE) {
      const fieldErrors: Record<string, string> = {};
      if (!trimmedName) fieldErrors.name = "Name is required";
      if (!trimmedPhone) fieldErrors.phone = "Phone is required";
      if (!trimmedPostcode) fieldErrors.postcode = "Postcode is required";
      if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        fieldErrors.email = "Please enter a valid email address";
      }
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        setStatus("idle");
        if (fieldErrors.name) setStep(1);
        else if (fieldErrors.phone || fieldErrors.email) setStep(2);
        else if (fieldErrors.postcode) setStep(3);
        return;
      }

      const remedialLabels = REMEDIAL_WORKS.filter((r) => remedialWorks.has(r.key)).map(
        (r) => r.label
      );
      const measureLabels = RETROFIT_MEASURE_TILES.filter((m) => measures.has(m.key)).map(
        (m) => m.label
      );

      const composed = [
        "Route: Retrofit Assessment Quotation",
        `Remedial works: ${remedialLabels.length ? remedialLabels.join(", ") : "None reported"}`,
        `Recommended measures: ${measureLabels.length ? measureLabels.join(", ") : "Not specified"}`,
        userMessage ? `\nNote: ${userMessage}` : null,
      ]
        .filter(Boolean)
        .join("\n");

      try {
        let attachment:
          | {
              fileName: string;
              contentType:
                | "application/pdf"
                | "application/msword"
                | "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
              base64: string;
            }
          | undefined;
        if (assessmentFile) {
          attachment = {
            fileName: assessmentFile.name,
            contentType: assessmentFile.type as
              | "application/pdf"
              | "application/msword"
              | "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            base64: await readFileAsBase64(assessmentFile),
          };
        }

        const result = await submitRetrofit({
          data: {
            name: trimmedName,
            phone: trimmedPhone,
            email: trimmedEmail || "",
            postcode: trimmedPostcode,
            property_type: "Retrofit Assessment Quotation",
            message: composed,
            consent: true,
            source_page: source,
            gclid: getStoredGclid(),
            honeypot,
            attachment,
          },
        });

        if (result.success) {
          if (result.id) crsLeadSubmitted({ leadId: result.id, formId: "home_hero_grants", leadType: "grants" });
          setMessage("");
          setRemedialWorks(new Set());
          setMeasures(new Set());
          setAssessmentFile(null);
          setSelectedService("");
          setStatus("success");
          toast.success("Enquiry sent", {
            description: "Thanks - we'll review your assessment and be in touch shortly.",
          });
          navigate({ to: "/thank-you" });
        } else {
          setFormError(result.error ?? "Something went wrong. Please try again.");
          setStatus("error");
        }
      } catch (err) {
        console.error(err);
        setFormError("Network error. Please call us on 0800 009 2992.");
        setStatus("error");
      }
      return;
    }

    const raw = {
      form_type: "survey" as const,
      name: trimmedName || undefined,
      phone: trimmedPhone || undefined,
      email: trimmedEmail || undefined,
      postcode: trimmedPostcode || undefined,
      service_of_interest: current.service || undefined,
      message: userMessage || undefined,
      source_page: source,
      gclid: getStoredGclid(),
      honeypot,
    };

    const parsed = submissionSchema.safeParse(raw);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "form");
        fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      setStatus("idle");
      if (fieldErrors.name) setStep(1);
      else if (fieldErrors.phone || fieldErrors.email) setStep(2);
      else if (fieldErrors.postcode) setStep(3);
      else if (fieldErrors.service_of_interest || fieldErrors.message) setStep(4);
      return;
    }

    try {
      const result = await submit({ data: parsed.data });
      if (result.success) {
        if (result.id) crsLeadSubmitted({ leadId: result.id, formId: "home_hero", leadType: "other" });
        setStatus("success");
        toast.success("Enquiry sent", {
          description: "Thanks - we'll be in touch shortly.",
        });
        navigate({ to: "/thank-you" });
      } else {
        setFormError(result.error ?? "Something went wrong. Please try again.");
        setStatus("error");
      }
    } catch (err) {
      console.error(err);
      setFormError("Network error. Please call us on 0800 009 2992.");
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div className={cn("rounded-2xl bg-card p-8 shadow-lift border", className)}>
        <div className="flex flex-col items-center text-center gap-3 py-6">
          <div className="rounded-full bg-success/10 p-3">
            <CheckCircle2 className="h-10 w-10 text-success" aria-hidden />
          </div>
          <h3 className="text-xl font-semibold">Thanks: We&apos;ll Be In Touch Shortly.</h3>
          <p className="text-sm text-muted-foreground">
            We&apos;ve received your enquiry and one of the team will be in touch shortly. For urgent
            matters please call us on{" "}
            <a href="tel:+448000092992" className="text-accent font-medium">
              0800 009 2992
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  const stepLabel = `Step ${step} of ${TOTAL_STEPS}`;
  const progressPct = Math.round((step / TOTAL_STEPS) * 100);

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}

      className={cn("rounded-2xl bg-card p-4 sm:p-6 md:p-7 shadow-lift border space-y-4", className)}
      noValidate
    >
      <div aria-hidden className="absolute left-[-9999px] w-px h-px overflow-hidden">
        <label>
          Company website
          <input
            type="text"
            name="company_website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-balance">{title}</h3>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        <div className="mt-3 text-xs text-muted-foreground">
          <span className="font-semibold uppercase tracking-wider">{stepLabel}</span>
        </div>
        <div className="mt-1.5 h-1.5 rounded-full bg-secondary overflow-hidden">
          <div
            className="h-full bg-accent transition-all duration-300 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <div>
        {step === 1 && (
          <StepShell heading="Let's start with your name">
            <StepField
              label="Name"
              required
              error={errors.name}
              inputProps={{
                type: "text",
                name: "name",
                value: name,
                onChange: (e) => setName(e.target.value),
                autoComplete: "name",
              }}
            />
          </StepShell>
        )}

        {step === 2 && (
          <StepShell heading="How can we contact you?">
            <StepField
              label="Email (optional)"
              error={errors.email}
              inputProps={{
                type: "email",
                name: "email",
                value: email,
                onChange: (e) => setEmail(e.target.value),
                autoComplete: "email",
              }}
            />
            <StepField
              label="Phone"
              required
              error={errors.phone}
              inputProps={{
                type: "tel",
                name: "phone",
                value: phone,
                onChange: (e) => setPhone(e.target.value),
                autoComplete: "tel",
              }}
            />
          </StepShell>
        )}

        {step === 3 && (
          <StepShell heading="Where is the property?">
            <StepField
              label="Postcode"
              required
              error={errors.postcode}
              inputProps={{
                type: "text",
                name: "postcode",
                value: postcode,
                onChange: (e) => setPostcode(e.target.value.toUpperCase()),
                autoComplete: "postal-code",
                maxLength: 20,
                autoFocus: true,
              }}
            />
          </StepShell>
        )}

        {step === 4 && (
          <StepShell heading="What can we help with?">
            <div>
              <label className="block text-sm font-medium mb-1.5">Service of interest</label>
              <select
                name="service_of_interest"
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-3.5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label="Service of interest"
              >
                <option value="">Not sure: please advise</option>
                {SERVICES.map((s) => (
                  <option key={s.slug} value={s.shortName}>
                    {s.shortName}
                  </option>
                ))}
                <option value={RETROFIT_ENQUIRY_OPTION_VALUE}>
                  {RETROFIT_ENQUIRY_OPTION_LABEL}
                </option>
              </select>
              {errors.service_of_interest && (
                <p className="mt-1 text-xs text-destructive">{errors.service_of_interest}</p>
              )}
            </div>

            {isRetrofit && (
              <div className="space-y-5 pt-1">
                <IconToggleGrid
                  label="Was any work required before your scheme installation can go ahead?"
                  helpText="If your retrofit assessment says any remedial or preparation work is needed before installation, please select the relevant items below."
                  options={REMEDIAL_WORKS}
                  selected={remedialWorks}
                  onToggle={(key) => toggleFromSet(setRemedialWorks, key)}
                />
                <IconToggleGrid
                  label="Which scheme measures were you recommended for?"
                  helpText="Please select the measures included in your retrofit assessment."
                  options={RETROFIT_MEASURE_TILES}
                  selected={measures}
                  onToggle={(key) => toggleFromSet(setMeasures, key)}
                />
                <div>
                  <label
                    htmlFor="home-retrofit-assessment-upload"
                    className="block text-sm font-semibold mb-1"
                  >
                    Upload Retrofit Assessment PDF
                  </label>
                  <p className="text-xs text-muted-foreground mb-2 leading-relaxed">
                    If you have already had a retrofit assessment completed, please upload the PDF
                    report here. This allows our team to review the recommendations and provide a more
                    accurate quotation before arranging a survey.
                  </p>
                  <div
                    className={cn(
                      "rounded-xl border border-dashed border-border bg-secondary/30 p-4",
                      errors.attachment && "border-destructive"
                    )}
                  >
                    <input
                      ref={fileInputRef}
                      id="home-retrofit-assessment-upload"
                      name="attachment"
                      type="file"
                      accept={GRANTS_ATTACHMENT_ACCEPT}
                      className="sr-only"
                      onChange={(ev) => onAssessmentFileChange(ev.target.files?.[0] ?? null)}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 rounded-full border border-input bg-background px-4 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors"
                    >
                      <Upload className="h-4 w-4" aria-hidden />
                      {assessmentFile ? "Change file" : "Choose file"}
                    </button>
                    {assessmentFile && (
                      <p className="mt-2 text-sm text-foreground">
                        {assessmentFile.name}{" "}
                        <span className="text-muted-foreground">
                          ({(assessmentFile.size / (1024 * 1024)).toFixed(1)} MB)
                        </span>
                      </p>
                    )}
                    <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                      PDF or Word, max {GRANTS_ATTACHMENT_MAX_BYTES / (1024 * 1024)}MB.
                    </p>
                  </div>
                  {errors.attachment && (
                    <p className="mt-1 text-xs text-destructive">{errors.attachment}</p>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1.5">
                {isRetrofit
                  ? "Anything else we should know? (optional)"
                  : "Anything we should know? (optional)"}
              </label>
              <textarea
                name="message"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-3.5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                maxLength={5000}
                aria-label="Message"
              />
            </div>
          </StepShell>
        )}
      </div>

      {formError && (
        <div
          className="flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
          <span>{formError}</span>
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 pt-1">
        {step > 1 && (
          <button
            type="button"
            onClick={goBack}
            disabled={status === "submitting"}
            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-background px-4 py-3 text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-60 sm:w-auto"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden /> Back
          </button>
        )}

        {step < TOTAL_STEPS && (
          <button
            type="button"
            onClick={goNext}
            className="w-full sm:ml-auto sm:w-auto inline-flex items-center justify-center gap-1.5 rounded-full bg-accent text-accent-foreground px-5 py-3 text-sm font-semibold hover:bg-accent/90 transition-colors shadow-cta"
          >
            Next <ArrowRight className="h-4 w-4" aria-hidden />
          </button>
        )}

        {step === TOTAL_STEPS && (
          <button
            type="submit"
            disabled={status === "submitting"}
            className="w-full sm:ml-auto sm:w-auto inline-flex items-center justify-center gap-1.5 rounded-full bg-accent text-accent-foreground px-5 py-3.5 text-sm font-semibold hover:bg-accent/90 transition-colors shadow-cta disabled:opacity-70"
          >
            {status === "submitting" ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Check className="h-4 w-4" aria-hidden />
            )}
            {isRetrofit ? "Send For Quotation" : "Request a callback"}
          </button>
        )}
      </div>

      <p className="text-xs text-muted-foreground text-center">
        By submitting you agree to our privacy policy. We never share your details.
      </p>
    </form>
  );
}

function StepShell({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3 animate-in fade-in slide-in-from-bottom-1 duration-200">
      <p className="text-sm font-semibold text-foreground">{heading}</p>
      {children}
    </div>
  );
}

function StepField({
  label,
  required,
  error,
  inputProps,
}: {
  label: string;
  required?: boolean;
  error?: string;
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
}) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">
        {label}
        {required && <span className="text-accent ml-0.5">*</span>}
      </label>
      <input
        {...inputProps}
        required={required}
        aria-invalid={!!error}
        aria-label={label}
        className={cn(
          "w-full rounded-lg border border-input bg-background px-3.5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-ring",
          error && "border-destructive focus:ring-destructive"
        )}
      />
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}

function IconToggleGrid({
  label,
  helpText,
  options,
  selected,
  onToggle,
}: {
  label: string;
  helpText: string;
  options: IconTile[];
  selected: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <p className="text-xs text-muted-foreground mb-2 leading-relaxed">{helpText}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {options.map((opt) => {
          const isSelected = selected.has(opt.key);
          const Icon: LucideIcon = opt.icon;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onToggle(opt.key)}
              aria-pressed={isSelected}
              className={cn(
                "flex flex-col items-center justify-center gap-1.5 rounded-xl border bg-card px-2 py-3 text-center text-xs font-medium text-foreground transition-colors",
                "hover:bg-secondary/60",
                isSelected
                  ? "border-accent bg-accent/10 ring-2 ring-accent"
                  : "border-border"
              )}
            >
              <Icon className="h-5 w-5" aria-hidden />
              <span className="leading-tight">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
